import { defineMessages, useIntl } from "react-intl";
import { classNames } from "@ct-react/core";
import { useLocaleFormatter } from "@ct-react/locale";
import { CartPaymentAmount, CartPaymentMode } from "@ct-react/calendar";
import {
  CartItemPrice as CartItemPriceModel,
  CartPrice as CartPriceModel,
  DownPayment,
  isCartItemPrice
} from "@shared/graphql";
import { DataProps } from "../../tools/components";
import { pricing } from "../../i18n/sharable-defs";
import InfoBox from "../common/info-box";
import "./price.scss";

const transDefs = defineMessages({
  detail: { id: "cart.price.detail-title",defaultMessage: "Détails du prix" },
  charge: { id: "cart.price.charge-title", defaultMessage: "Frais de paiement" },
  benefits: { id: "cart.price.benefits", defaultMessage: "Prestations supplémentaires" },
  lastMinute: { id: "cart.price.last-minute-warning", defaultMessage: "Le montant total devra être payé immédiatement." },
  toPay: { id: "cart.price.to-pay", defaultMessage: "Paiement immédiat" }
});

type Props = {
  price: CartPriceModel | CartItemPriceModel;
  downPayment?: DownPayment;
  toPay?: { mode: CartPaymentMode, detail: CartPaymentAmount }
}

const CartPrice = ({ className, data: { price, downPayment, toPay } }: DataProps<Props>) => {

  const intl = useIntl();
  const { print } = useLocaleFormatter();

  const wrapperClasses = classNames("cart-price", className);

  return (
    <>
      <div className={wrapperClasses}>

        <h2>{intl.formatMessage(isCartItemPrice(price) ? transDefs.detail : pricing.total)}</h2>
        {isCartItemPrice(price) &&
          <dl className="price-list">
            <dt>{intl.formatMessage(pricing.unit)}</dt>
            <dd>{print.price(price.bare)}</dd>
            {!!price.discount &&
              <>
                <dt>{intl.formatMessage(pricing.discount)}</dt>
                <dd className="discount">{print.price(price.discount)}</dd>
              </>
            }
            {(() => {
              const benefits = price.options.find(({ type }) => type === "BENEFIT");
              const taxes = price.options.find(({ type }) => type === "TOURIST_TAX");
              return (
                <>
                  {(!!benefits) &&
                    <>
                      <dt>{intl.formatMessage(transDefs.benefits)}</dt>
                      <dd>{print.price(benefits.amount)}</dd>
                    </>
                  }
                  {!!taxes &&
                    <>
                      <dt>{intl.formatMessage(pricing.taxes)}</dt>
                      <dd>{print.price(taxes.amount)}</dd>
                    </>
                  }
                </>);
            })()}
          </dl>
        }
        <dl className="price-list total">
          <dt>{intl.formatMessage(pricing.amount)}</dt>
          <dd>{print.price(price.amount)}</dd>
        </dl>
      </div>

      {!!(downPayment?.providerCharge) &&
        <div className={classNames(wrapperClasses, "charge")}>
          <h2>{intl.formatMessage(transDefs.charge)}</h2>
          <dl className="price-list">
            <dt>{intl.formatMessage(pricing.chargeLabel)}</dt>
            <dd>{intl.formatMessage(pricing.charge, {
              adaptive: downPayment.rate < 1,
              amount: print.price(downPayment.providerCharge)
            })}</dd>
          </dl>
        </div>
      }
      {downPayment?.rate === 1 && <InfoBox msg={intl.formatMessage(transDefs.lastMinute)} />}

      {!!toPay &&
        <div className={classNames(wrapperClasses, "to-pay")}>
          <h2>{intl.formatMessage(transDefs.toPay)}</h2>
          <dl className="price-list">
            <dt>{intl.formatMessage(toPay.mode === "full" ? pricing.total : pricing.down)}</dt>
            <dd>{print.price(toPay.detail.detailed.booking)}</dd>
            {!!toPay.detail.detailed.providerCharge &&
              <>
                <dt>{intl.formatMessage(pricing.chargeLabel)}</dt>
                <dd>{print.price(toPay.detail.detailed.providerCharge)}</dd>
              </>
            }
          </dl>
          <dl className="price-list total">
            <dt>{intl.formatMessage(pricing.amount)}</dt>
            <dd>{print.price(toPay.detail.total)}</dd>
          </dl>
        </div>
      }

    </>);

};

export default CartPrice;
